import React, { useCallback, useRef, useState } from 'react';
import {
	type AnalyticsEventPayload,
	type UIAnalyticsEvent,
	useAnalyticsEvents,
} from '@atlaskit/analytics-next';
import PremiumIcon from '@atlaskit/icon/glyph/premium';
import { P400 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import type { ProjectType } from '@atlassian/jira-common-constants';
import { type FlagComponentProps, AkFlag, toFlagId, useFlagsService } from '@atlassian/jira-flags';
import { useIntl } from '@atlassian/jira-intl';
import {
	ContextualAnalyticsData,
	FireScreenAnalytics,
	fireUIAnalytics,
	SCREEN,
} from '@atlassian/jira-product-analytics-bridge';
import { API_CLICKED, SEND_UI_EVENT, SUBJECT_BUTTON, SUBJECT_REFRESH_BTN } from '../../constants';
import messages from './messages';
import { getApplicationName } from './utils';

const SCREEN_NAME = 'upgradedFlag';
const UPGRADE_ONBOARDING_REFRESH_FLAG_ID = 'upgradeOnboardingRefresh';

type Props = {
	projectType: ProjectType;
	onDismissed?: FlagComponentProps['onDismissed'];
};

const sendUIEvent = (
	createAnalyticsEvent: (payload: AnalyticsEventPayload) => UIAnalyticsEvent,
) => {
	const analyticsSetSuccessEvent = createAnalyticsEvent({
		type: SEND_UI_EVENT,
		action: API_CLICKED,
		actionSubject: SUBJECT_BUTTON,
		actionSubjectId: SUBJECT_REFRESH_BTN,
	});
	return fireUIAnalytics(analyticsSetSuccessEvent, SUBJECT_REFRESH_BTN);
};

/**
 * Example Usage:
 * const { showFlag } = useRefreshFlag(SERVICE_DESK_PROJECT);
 * showFlag();
 */
export const useRefreshFlag = (projectType: ProjectType) => {
	const { showFlag, dismissFlag } = useFlagsService();
	const [flagVisible, setFlagVisible] = useState(false);
	const flagId = useRef();

	const doShowFlag = useCallback(() => {
		if (flagVisible) {
			return;
		}

		setFlagVisible(true);

		showFlag(() => (
			<RefreshFlag
				projectType={projectType}
				onDismissed={() => flagId.current && dismissFlag(flagId.current)}
			/>
		));
	}, [flagVisible, showFlag, projectType, dismissFlag]);

	return { showFlag: doShowFlag };
};

/**
 * Example Usage:
 * import { useFlagsService } from '@atlassian/jira-flags';
 * const { showFlag } = useFlagsService();
 *
 * showFlag(() => <RefreshFlag projectType={SERVICE_DESK_PROJECT} />)
 */
export const RefreshFlag = ({ projectType }: Props) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();

	const onClick = useCallback(() => {
		// this analytics event isn't 100 % reliable since it is
		// possible the page gets reloaded before the event get sent
		sendUIEvent(createAnalyticsEvent);
		// reload page
		window.location.reload();
	}, [createAnalyticsEvent]);

	return (
		<ContextualAnalyticsData sourceName={SCREEN_NAME} sourceType={SCREEN}>
			<AkFlag
				icon={
					<PremiumIcon
						primaryColor={token('color.icon.accent.purple', P400)}
						label={formatMessage(messages.iconLabel)}
					/>
				}
				description={formatMessage(messages.contentText)}
				id={toFlagId(UPGRADE_ONBOARDING_REFRESH_FLAG_ID)}
				title={formatMessage(messages.title, { product: getApplicationName(projectType) })}
				actions={[
					{
						content: formatMessage(messages.refreshPage),
						onClick,
					},
				]}
			/>
			<FireScreenAnalytics />
		</ContextualAnalyticsData>
	);
};
